import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { REGION_CODE } from '@gh/config';
import {
  DialogService,
  FormMode,
  NotificationService,
  WaitingService,
  yesNoConfirmation,
  Confirmation,
} from '@gh/core-ui';
import { productDetails } from '../../resources/product-list-item.model';
import {
  ActionFlow,
  AppRouter,
  getRouteInitData,
  TextDictionary,
} from '@gh/common-shared';
import { createEnum } from '@gh/core-data';
import { ProductListBusinessService } from '../../resources/product-list-business.service'
import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  FormState,
  FormStateBuilder,
  UnpaidInvoicesDialogComponent,
  UnpaidInvoicesDialogData,
} from '@gh/builder-shared';

createEnum('LANGUAGE', {
  en_US: 'EN_US',
  en_AU: 'EN_AU',
  en_NZ: 'EN_NZ',
});


@Component({
  standalone: false,
  selector: 'gh-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
  providers: [FormActionPipelines, CommonActions]
})

export class ProductDetailsComponent extends AbstractFormComponent<productDetails> implements OnInit {
  states: TextDictionary;
  creationMode: boolean = false;
  form: FormState<productDetails>;
  timeZones: TextDictionary;
  private _currentTab: string = 'common';
  private pipelines: FormActionPipelines;
  private commonActions: CommonActions;
  isDeactivated = false; 
  originalProduct: productDetails | undefined; 
  loading = false;

  constructor(
    private fb: FormBuilder,
    private dialogs: DialogService,
    private router: AppRouter,
    private route: ActivatedRoute,
    private navigationService: BuilderNavigationService,
    private formBuilder: FormStateBuilder,
    public businessService: ProductListBusinessService,
    private notificationService: NotificationService,
    pipelines: FormActionPipelines,
    commonActions: CommonActions,
    private dialogService: DialogService,
    private waitings: WaitingService,
  ) {
    super();
    this.pipelines = pipelines;
    this.commonActions = commonActions;
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(paramMap => {
      const productId = paramMap.get('id');
      this.creationMode = !productId || productId === 'new';

      const routeInit = getRouteInitData(this.route) || {};
      const { product, ...init } = routeInit;
      Object.assign(this, init);

    const defaultProduct: productDetails = {
      name: '',
      fee: null as any,
      currency: '',
      id: ''
    };

      this.form = this.formBuilder.buildFunctional(product || defaultProduct, (value, { root }) => root({
      mode: this.creationMode ? FormMode.CreateMode : FormMode.EditMode,
      optimisticLock: true,
      children: {
          id: {}, 
        name: { required: true },
        fee: { required: true },
          currency: {}
      }
    }));

      // If not creation mode and no product in route data, fetch from API
      if (!this.creationMode && !product) {
        if (productId) {
          this.loading = true;
          this.businessService.get(productId).subscribe((product: productDetails) => {
            this.loading = false;
            if (product) {
              this.form.setValue(product);
              this.originalProduct = { ...product }; // <-- Store original
              this.isDeactivated = product.active === false; // <-- Set deactivated state
              if (this.isDeactivated) {
                this.form.group.disable(); // <-- Disable the form
              }
            }
          }, () => this.loading = false);
        }
      } else if (!this.creationMode && product) {
        this.originalProduct = { ...product }; // <-- Store original if provided in route data
      }
    });
  }

  get completeFlow(): ActionFlow<FormFlowState<productDetails>> {
    return this.pipelines.complete({
      noSuccessNotification: true,
      execute: this.commonActions.execute((state) => {
        if (this.creationMode) {
          return this.businessService.create(<any>state.value);
        } else {
          const product = { ...this.form.value };
          delete product.etag;
          return this.businessService.update(product);
        }
      }),
      finish: (state: FormFlowState<productDetails>) => {
        const { value, form } = state;

        if (value.unpaidInvoices && value.unpaidInvoices.length > 0) {
          this.showUnpaidInvoicesWarning(value);
          return;
        }

        this.notificationService.success('msg_saved_successfully');

        if (this.creationMode) {
          this.router.switchTo(['/setup/product/details', value.id]);
        } else {
          (<FormState<productDetails>>form).setValue(value);
          this.originalProduct = { ...value }; // <-- Update originalProduct after save
        }
      },
    });
  }

  private showUnpaidInvoicesWarning(product: productDetails): void {
    const input: UnpaidInvoicesDialogData = {
      unpaidInvoices: product.unpaidInvoices!,
      productName: product.name,
      customMessage: 'This subscription plan has pending unpaid invoices. Please review and settle them before updating the subscription fee.',
      showProceedButton: false
    };

    const dialogRef = this.dialogService.openWithInput(UnpaidInvoicesDialogComponent, input, { disableClose: true });

    dialogRef.afterClosed().subscribe((acknowledged) => {
      if (acknowledged) {
        const payload = { ...this.form.value };
        delete payload.etag;
        this.businessService.update(payload, true).pipe(this.waitings.operator()).subscribe((result) => {
          this.form.setValue(result);
          this.originalProduct = { ...result };
          this.notificationService.success('msg_saved_successfully');
        }, this.notificationService.processAll);
      } else {
        // User cancelled, fetch the latest data to reset
        this.businessService.get(product.id as string).pipe(this.waitings.operator()).subscribe((latest) => {
          this.form.setValue(latest);
          this.originalProduct = { ...latest };
        }, this.notificationService.processAll);
      }
    });
  }

  onProcess() {
    const currentFee = this.form.get('fee')?.value;

    if (this.creationMode) {
      if (currentFee === 0) {
        this.notificationService.error('msg_product_cannot_be_created_with_zero_fee');
        return;
      }
      this._processForm();
      return;
    }

    const originalFee = this.originalProduct?.fee;

    if (currentFee !== originalFee) {
      if (currentFee === 0) {
        this.dialogService.confirmation(yesNoConfirmation('msg_product_fee_update_to_zero'))
          .subscribe((result: Confirmation) => {
            if (result === Confirmation.Yes) {
              this._processForm();
            }
          });
      } else {
        const message = `Changing the fee will schedule the cancellation of the All Office subscriptions at the end of their billing period and create a new subscription with the updated fee (${Number(currentFee).toFixed(2)}).The Custom office subscriptions will not be affected. Do you want to continue?`;
        this.dialogService.confirmation(yesNoConfirmation(message))
          .subscribe((result: Confirmation) => {
            if (result === Confirmation.Yes) {
              this._processForm();
            }
          });
      }
    } else {
      this._processForm();
    }
  }

  private _processForm() {
    if (!this.form.invalid) {
      this.completeFlow.run({
        value: this.form.value,
        form: this.form,
        trigger: 'process'
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

  onAdd(): void {
    this.router.switchTo(['/setup/product/details/new']);  
  }

  onTabChanged(name: string): void {
    if (this._currentTab !== name) {
      this._currentTab = name;
    }
  }
  checkRegion(regionCode: string): boolean {
    return regionCode === REGION_CODE;
  }

  get currentTab(): string {
    return this._currentTab;
  }

  minDate(type: string): Date {
    return this.form.get(type)?.value;
  }

  goToAel(entityId?: string, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'PRODUCT',
      label: label ? label : 'lbl_product',
    });
  }


  isUnchanged(): boolean {
    // In create mode, always allow processing if the form is valid
    if (this.creationMode) return false;
    if (!this.originalProduct) return true;
    const current = this.form.value;
    return (
      current.name === this.originalProduct.name &&
      current.fee === this.originalProduct.fee &&
      current.currency === this.originalProduct.currency
    );
  }
}

